import React, { VFC, useEffect, useMemo } from 'react';

import { ICartEntry, ICombo } from '@rbi-ctg/menu';
import { OrderSummary } from 'components/product-detail/order-summary';
import { PickerAspectGroup } from 'components/product-detail/picker-aspect-group';
import { MenuObjectTypes } from 'enums/menu';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { useMParticleContext } from 'state/mParticle';
import { useMenuContext } from 'state/menu';
import { useMenuUIContext } from 'state/menu-ui';
import { useProductWizardContext } from 'state/product-wizard';
import { useEditingCartEntry } from 'state/product-wizard/hooks/use-editing-cart-entry';
import { ProductWizardUtils } from 'state/product-wizard/utils';
import { transformMenuObjectToCartItem } from 'utils/wizard';

import { ProductUpsell } from '../product-upsell';
import {
  transformUserSelectionsComboToComboSlotSelections,
  transformUserSelectionsModifierToModifierSelection,
} from '../utils';

import { ProductSelectionsContainer } from './product-home.styled';

export interface IProductHomeProps {
  homePositionRef?: React.MutableRefObject<number>;
  showStaticPickers?: boolean;
}

/**
 * The summary of the Product Wizard.
 * NOTE: this component is rendered on the DOM even when the user is viewing another step on the wizard.
 * Do not rely on unmount principles, because it does not get unmounted.
 * The fixed buttons in mobile are removed to avoid collisions with overlays.
 */
export const ProductHome: VFC<IProductHomeProps> = ({ homePositionRef, showStaticPickers }) => {
  const { location } = useLocationContext();
  const { isOffer, priceForItemOptionModifier, pricingFunction, priceForComboSlotSelection } =
    useMenuContext();
  const { setActiveCartEntries } = useMenuUIContext();
  const mParticle = useMParticleContext();
  const { menuObject, productQuantity, userSelections } = useProductWizardContext();

  const useEnableMenuUpsellItems = useFlag(LaunchDarklyFlag.ENABLE_MENU_UPSELL_ITEMS);

  const editingCartEntry = useEditingCartEntry();

  const computedProduct = useMemo(
    () => ProductWizardUtils.ComputeSelectedOption(menuObject, userSelections.pickerAspects),
    [menuObject, userSelections]
  );

  const { mainEntry, upsellEntries } = useMemo(
    () =>
      transformMenuObjectToCartItem(
        computedProduct,
        priceForItemOptionModifier,
        pricingFunction,
        priceForComboSlotSelection,
        {
          quantity: productQuantity,
          modifierSelections: transformUserSelectionsModifierToModifierSelection(userSelections),
          pickerSelections: userSelections.pickerAspects,
          comboSlotSelections: transformUserSelectionsComboToComboSlotSelections(
            userSelections.comboSlot,
            computedProduct as ICombo
          ),
          location,
          // quickConfigs, // ignore for now - only TH uses quickConfigs
          existingCartId: editingCartEntry?.cartId,
          shouldLogEvents: isOffer,
          mParticle,
        }
      ),
    [
      computedProduct,
      editingCartEntry,
      isOffer,
      location,
      mParticle,
      priceForComboSlotSelection,
      priceForItemOptionModifier,
      pricingFunction,
      productQuantity,
      userSelections,
    ]
  );

  useEffect(() => {
    setActiveCartEntries([mainEntry, ...upsellEntries] as ICartEntry[]);
  }, [mainEntry, setActiveCartEntries, upsellEntries]);

  return (
    <>
      <ProductSelectionsContainer data-testid="product-home-customization-panel">
        {menuObject._type === MenuObjectTypes.PICKER && (
          <PickerAspectGroup pickerData={menuObject} />
        )}
        {!showStaticPickers && <OrderSummary homePositionRef={homePositionRef} />}
      </ProductSelectionsContainer>
      {useEnableMenuUpsellItems && !showStaticPickers && <ProductUpsell />}
    </>
  );
};

export default ProductHome;
